import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Stack,
} from '@mui/material'
import { Add, Edit, Delete, Visibility, CheckCircle, Warning, Schedule, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, ExportButtons, PermissionGate } from '@components/common'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const QA_TYPES = [
  { value: 'daily', label: 'Daily QA/QC' },
  { value: 'weekly', label: 'Weekly QA/QC' },
  { value: 'monthly', label: 'Monthly QA/QC' },
  { value: 'quarterly', label: 'Quarterly QA/QC' },
  { value: 'annual', label: 'Annual QA/QC' },
]

const RESULT_OPTIONS = [
  { value: 'pass', label: 'Pass', color: 'success' },
  { value: 'fail', label: 'Fail', color: 'error' },
  { value: 'conditional', label: 'Conditional Pass', color: 'warning' },
  { value: 'pending_review', label: 'Pending Review', color: 'info' },
]

const QARecordsList = () => {
  const { canCreate, canEdit, canDelete, hasPermission } = usePermissions()
  const [records, setRecords] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [saving, setSaving] = useState(false)
  const [complianceSummary, setComplianceSummary] = useState([])
  const [showFilters, setShowFilters] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    qa_type: '',
    overall_result: '',
  })

  const [formData, setFormData] = useState({
    asset: '',
    qa_type: 'daily',
    qa_date: '',
    due_date: '',
    overall_result: 'pending_review',
    corrective_action: '',
    follow_up_required: false,
    tools_used: '',
    tool_calibration_id: '',
    notes: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.qa_type) params.qa_type = filters.qa_type
      if (filters.overall_result) params.overall_result = filters.overall_result

      const [recordsRes, assetsRes, complianceRes] = await Promise.all([
        api.get('/maintenance/qa-records/', { params }),
        api.get('/assets/assets/'),
        api.get('/maintenance/qa-records/compliance_summary/'),
      ])
      setRecords(recordsRes.data.results || recordsRes.data)
      setAssets(assetsRes.data.results || assetsRes.data)
      setComplianceSummary(complianceRes.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load QA/QC records')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  // Export columns definition
  const exportColumns = useMemo(() => [
    { field: 'qa_date', headerName: 'QA/QC Date', renderValue: (v) => formatDate(v) || '-' },
    { field: 'asset_name', headerName: 'Equipment' },
    { field: 'qa_type', headerName: 'QA/QC Type', renderValue: (v) => QA_TYPES.find((t) => t.value === v)?.label || v },
    { field: 'overall_result', headerName: 'Result', renderValue: (v) => RESULT_OPTIONS.find((o) => o.value === v)?.label || v },
    { field: 'tools_used', headerName: 'Tools Used' },
    { field: 'tool_calibration_id', headerName: 'Tool Cal. ID' },
    { field: 'performed_by_name', headerName: 'Performed By' },
    { field: 'reviewed_by_name', headerName: 'Reviewed By' },
    { field: 'follow_up_required', headerName: 'Follow-up', renderValue: (v) => v ? 'Required' : 'No' },
  ], [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({ qa_type: '', overall_result: '' })
  }

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length

  const handleAdd = () => {
    setSelectedRecord(null)
    const today = new Date().toISOString().split('T')[0]
    setFormData({
      asset: '',
      qa_type: 'daily',
      qa_date: today,
      due_date: today,
      overall_result: 'pending_review',
      corrective_action: '',
      follow_up_required: false,
      tools_used: '',
      tool_calibration_id: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (record) => {
    setSelectedRecord(record)
    setFormData({
      asset: record.asset || '',
      qa_type: record.qa_type || 'daily',
      qa_date: record.qa_date || '',
      due_date: record.due_date || '',
      overall_result: record.overall_result || 'pending_review',
      corrective_action: record.corrective_action || '',
      follow_up_required: record.follow_up_required || false,
      tools_used: record.tools_used || '',
      tool_calibration_id: record.tool_calibration_id || '',
      notes: record.notes || '',
    })
    setFormOpen(true)
  }

  const handleView = (record) => {
    setSelectedRecord(record)
    setDetailOpen(true)
  }

  const handleSave = async () => {
    if (!formData.asset || !formData.qa_date) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date || formData.qa_date,
      }
      if (selectedRecord) {
        await api.patch(`/maintenance/qa-records/${selectedRecord.id}/`, payload)
        toast.success('QA/QC record updated')
      } else {
        await api.post('/maintenance/qa-records/', payload)
        toast.success('QA/QC record created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving record:', error)
      toast.error(error.response?.data?.error || 'Failed to save QA/QC record')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete this QA/QC record?`)) return
    try {
      await api.delete(`/maintenance/qa-records/${record.id}/`)
      toast.success('QA/QC record deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting record:', error)
      toast.error('Failed to delete QA/QC record')
    }
  }

  const handleReview = async (record, result) => {
    try {
      await api.post(`/maintenance/qa-records/${record.id}/review/`, { result })
      toast.success('QA/QC record reviewed')
      fetchData()
    } catch (error) {
      console.error('Error reviewing record:', error)
      toast.error('Failed to review QA/QC record')
    }
  }

  const getResultColor = (result) => {
    const option = RESULT_OPTIONS.find((o) => o.value === result)
    return option?.color || 'default'
  }

  const columns = [
    {
      field: 'qa_date',
      headerName: 'QA/QC Date',
      renderCell: (v) => formatDate(v) || '-',
      sortable: true,
    },
    { field: 'asset_name', headerName: 'Equipment', sortable: true },
    {
      field: 'qa_type',
      headerName: 'QA/QC Type',
      renderCell: (v) => (
        <Chip
          label={QA_TYPES.find((t) => t.value === v)?.label || v}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'overall_result',
      headerName: 'Result',
      renderCell: (v) => (
        <Chip
          label={RESULT_OPTIONS.find((o) => o.value === v)?.label || v}
          size="small"
          color={getResultColor(v)}
        />
      ),
    },
    { field: 'performed_by_name', headerName: 'Performed By' },
    { field: 'reviewed_by_name', headerName: 'Reviewed By', renderCell: (v) => v || '-' },
    {
      field: 'follow_up_required',
      headerName: 'Follow-up',
      renderCell: (v) => v ? <Chip label="Required" size="small" color="warning" /> : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          {row.overall_result === 'pending_review' && hasPermission('qa_records.review') && (
            <IconButton
              size="small"
              color="success"
              onClick={(e) => { e.stopPropagation(); handleReview(row, 'pass') }}
              title="Approve"
            >
              <CheckCircle fontSize="small" />
            </IconButton>
          )}
          {canEdit('qa_records') && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {canDelete('qa_records') && (
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  // Calculate stats
  const totalRecords = records.length
  const passedRecords = records.filter((r) => r.overall_result === 'pass').length
  const pendingReview = records.filter((r) => r.overall_result === 'pending_review').length
  const failedRecords = records.filter((r) => r.overall_result === 'fail').length
  const complianceRate = totalRecords > 0 ? Math.round((passedRecords / totalRecords) * 100) : 0

  return (
    <Box>
      <PageHeader
        title="QA/QC Records"
        subtitle="Daily, Monthly, and Annual Quality Assurance/Quality Control records"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Oncology' },
          { label: 'QA/QC Records' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <ExportButtons
              data={records}
              columns={exportColumns}
              title="QA/QC Records"
              filename="qa_qc_records"
              filters={{
                'QA Type': filters.qa_type ? QA_TYPES.find(t => t.value === filters.qa_type)?.label : null,
                Result: filters.overall_result ? RESULT_OPTIONS.find(o => o.value === filters.overall_result)?.label : null,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={activeFilterCount > 0 ? 'primary' : 'inherit'}
            >
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
            {canCreate('qa_records') && (
              <Button startIcon={<Add />} onClick={handleAdd}>
                Add QA/QC Record
              </Button>
            )}
          </Stack>
        }
      />

      {/* Filters Panel */}
      {showFilters && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>QA/QC Type</InputLabel>
                <Select
                  value={filters.qa_type}
                  onChange={(e) => handleFilterChange('qa_type', e.target.value)}
                  label="QA/QC Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {QA_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Result</InputLabel>
                <Select
                  value={filters.overall_result}
                  onChange={(e) => handleFilterChange('overall_result', e.target.value)}
                  label="Result"
                >
                  <MenuItem value="">All</MenuItem>
                  {RESULT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={3}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="primary">
                {complianceRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compliance Rate
              </Typography>
              <LinearProgress
                variant="determinate"
                value={complianceRate}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
                color={complianceRate >= 90 ? 'success' : complianceRate >= 70 ? 'warning' : 'error'}
              />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="success.main">
                {passedRecords}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Passed
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="info.main">
                {pendingReview}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Review
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="error.main">
                {failedRecords}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {pendingReview > 0 && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<Schedule />}>
          {pendingReview} QA/QC record(s) pending review
        </Alert>
      )}

      {failedRecords > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<Warning />}>
          {failedRecords} QA/QC record(s) failed - corrective action required
        </Alert>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={records}
          loading={loading}
          searchPlaceholder="Search QA/QC records..."
          emptyMessage="No QA/QC records found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedRecord ? 'Edit QA/QC Record' : 'Add QA/QC Record'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedRecord ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Equipment</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Equipment"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>QA/QC Type</InputLabel>
              <Select
                value={formData.qa_type}
                onChange={(e) => setFormData({ ...formData, qa_type: e.target.value })}
                label="QA/QC Type"
              >
                {QA_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Result</InputLabel>
              <Select
                value={formData.overall_result}
                onChange={(e) => setFormData({ ...formData, overall_result: e.target.value })}
                label="Result"
              >
                {RESULT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="QA/QC Date"
              type="date"
              value={formData.qa_date}
              onChange={(e) => setFormData({ ...formData, qa_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tools Used"
              value={formData.tools_used}
              onChange={(e) => setFormData({ ...formData, tools_used: e.target.value })}
              placeholder="e.g., Ion chamber, Electrometer, Phantom"
              helperText="Equipment/tools used for this QA/QC"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tool Cal. ID"
              value={formData.tool_calibration_id}
              onChange={(e) => setFormData({ ...formData, tool_calibration_id: e.target.value })}
              placeholder="e.g., CAL-2024-001, PTW-12345"
              helperText="Calibration certificate number(s) for tools"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Follow-up Required?</InputLabel>
              <Select
                value={formData.follow_up_required}
                onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.value })}
                label="Follow-up Required?"
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(formData.overall_result === 'fail' || formData.follow_up_required) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Corrective Action"
                value={formData.corrective_action}
                onChange={(e) => setFormData({ ...formData, corrective_action: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="QA/QC Record Details"
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
        }
      >
        {selectedRecord && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Equipment</Box>
                <Box fontWeight={600}>{selectedRecord.asset_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">QA/QC Type</Box>
                <Chip
                  label={QA_TYPES.find((t) => t.value === selectedRecord.qa_type)?.label}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">QA/QC Date</Box>
                <Box fontWeight={600}>
                  {formatDate(selectedRecord.qa_date) || '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Result</Box>
                <Chip
                  label={RESULT_OPTIONS.find((o) => o.value === selectedRecord.overall_result)?.label}
                  size="small"
                  color={getResultColor(selectedRecord.overall_result)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            {selectedRecord.tools_used && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Tools Used</Box>
                  <Box fontWeight={600}>{selectedRecord.tools_used}</Box>
                </Box>
              </Grid>
            )}
            {selectedRecord.tool_calibration_id && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Tool Cal. ID</Box>
                  <Box fontWeight={600}>{selectedRecord.tool_calibration_id}</Box>
                </Box>
              </Grid>
            )}
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Performed By</Box>
                <Box fontWeight={600}>{selectedRecord.performed_by_name || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Reviewed By</Box>
                <Box fontWeight={600}>{selectedRecord.reviewed_by_name || '-'}</Box>
              </Box>
            </Grid>
            {selectedRecord.follow_up_required && (
              <Grid item xs={12}>
                <Chip label="Follow-up Required" color="warning" />
              </Grid>
            )}
            {selectedRecord.corrective_action && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Corrective Action</Box>
                  <Box mt={0.5}>{selectedRecord.corrective_action}</Box>
                </Box>
              </Grid>
            )}
            {selectedRecord.notes && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Notes</Box>
                  <Box mt={0.5}>{selectedRecord.notes}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default QARecordsList
